import React, { type FC, useMemo, useSyncExternalStore } from "react";
import { toast } from "@heroui/react";
import { FormattedMessage } from "react-intl";

// Tiny pub/sub so the single <SkrLoaderToast /> rendered inside the toast.promise loading slot
// re-renders whenever progress changes - without closing/reopening the toast.
const listeners = new Set<() => void>();
let currentProgress = 0;

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const getSnapshot = () => currentProgress;

export const setSkrLoaderProgress = (progress: number): void => {
    currentProgress = progress;
    listeners.forEach((fn) => fn());
};

export const SkrLoaderToast: FC = () => {
    const p = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const values = useMemo(() => ({ progress: p }), [p]);
    return (
        <span>
            <FormattedMessage id="app.domain.skrLoading" values={values} />
        </span>
    );
};

/**
 * Wraps the SKR load in a SINGLE toast: a live-progress loading state that auto-transitions to
 * success/error (HeroUI `toast.promise`). Progress is pushed via `setSkrLoaderProgress` while the
 * promise is in flight; the returned promise is the input promise unchanged, so the caller still
 * gets the resolved value (or the rejection).
 */
export const withSkrLoaderToast = <T,>(promise: Promise<T>): Promise<T> => {
    toast.promise(promise, {
        loading: <SkrLoaderToast />,
        success: <FormattedMessage id="app.domain.skrLoaded" />,
        error: (e: Error) => e.message,
    });
    return promise;
};
